<template>
    <div>
        <template v-if="chatType === 'OBJECT'">
            <div class="messages-container" id="messages-container">
                <div
                    v-for="message in messages"
                    :class="`d-flex justify-${
                        message.sender.id !== currentUser.id ? 'start' : 'end'
                    } mx-2`"
                    :key="message.id"
                >
                    <div>
                        <small>
                            <template v-if="message.sender.id === currentUser.id">
                                <b>{{ getFormattedSenderName(message).name }}</b
                                ><span>{{ getFormattedSenderName(message).separator }}</span>

                                <div :class="'message-createdAt'">
                                    {{ getDate(message.createdAt) }}
                                </div>
                            </template>
                            <template v-else>
                                <b>{{ getFormattedSenderName(message).name }}</b
                                ><span>{{ getFormattedSenderName(message).separator }}</span>

                                {{ getDate(message.createdAt) }}
                            </template>
                        </small>
                        <div
                            :class="
                                message.sender.id !== currentUser.id
                                    ? 'otherMessages'
                                    : 'myMessages'
                            "
                        >
                            {{ message.text }}
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div
                v-for="message in filteredMessages"
                class="d-flex pt-2 font-weight-regular message"
                style="color: black"
                :key="message.id"
            >
                <div
                    v-if="isNotFromCurrentUser(message) || starredOnly"
                    :class="
                        isNotFromCurrentUser(message) || starredOnly
                            ? 'order-1 mr-4'
                            : 'order-3 ml-4'
                    "
                >
                    <v-avatar :color="getNameColor(message)" size="x-small">
                        {{ getUserBigram(message.sender) }}
                    </v-avatar>
                </div>
                <div class="order-2 main-content">
                    <div>
                        <small>
                            <b>{{ getFormattedSenderName(message).name }}</b>
                            <span>{{ getFormattedSenderName(message).separator }}</span>
                            {{ getDate(message.createdAt) }}
                            <v-tooltip v-if="message.starred" location="bottom">
                                <template v-slot:activator="{ props }">
                                    <v-icon
                                        size="x-small"
                                        v-bind="props"
                                        class="mx-1"
                                        color="primary"
                                    >
                                        $fa-star
                                    </v-icon>
                                </template>
                                <span>Important!</span>
                            </v-tooltip>
                        </small>
                        <div
                            :class="
                                isNotFromCurrentUser(message)
                                    ? 'position-relative otherMessages'
                                    : 'position-relative myMessages'
                            "
                        >
                            {{ message.text }}
                            <div :class="getMessageOptionsClass(message)">
                                <div>
                                    <v-tooltip location="bottom">
                                        <template v-slot:activator="{ props }">
                                            <v-btn-icon
                                                v-bind="props"
                                                :icon="
                                                    message.starred ? '$far-fa-star' : '$fa-star'
                                                "
                                                :color="message.starred ? 'secondary' : 'primary'"
                                                @click="toggleMessageStar(message)"
                                            >
                                            </v-btn-icon>
                                        </template>
                                        <span>{{
                                            message.starred
                                                ? "Retirer des messages importants."
                                                : "Ajouter aux messages importants."
                                        }}</span>
                                    </v-tooltip>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="!starredOnly"
                    :class="
                        isNotFromCurrentUser(message)
                            ? 'order-3 flex-grow-1'
                            : 'order-1 flex-grow-1'
                    "
                >
                    <!-- Empty space -->
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import { getUserDisplayName } from "@/services/userServices";
import { User } from "../../store/store";
import STAR_CHAT_MESSAGE from "@/graphql/StarChatMessage.gql";
import moment from "moment";
import { Component, Vue, toNative, Watch } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";

@Component
export class ChatConversation extends Vue {
    @Prop({ default: "OBJECT" }) chatType: string;
    @Prop({ default: [] }) messages: Array<any>;
    @Prop({ default: false }) starredOnly: Boolean;

    get currentUser(): User {
        return this.$store.state.user;
    }

    get filteredMessages() {
        if (!this.messages) {
            console.warn("Messages container non initialisé !");
            return []; // Retourne un tableau vide pour éviter les erreurs
        }
        return this.starredOnly ? this.messages.filter((m) => m.starred) : this.messages;
    }

    getUserBigram(user) {
        if (user) {
            return (
                (user.firstname ? user.firstname[0] : "") + (user.lastname ? user.lastname[0] : "")
            );
        }
        return "XX";
    }

    isNotFromCurrentUser(messageObj) {
        return messageObj.sender?.id !== this.currentUser?.id;
    }

    getDate(date: string) {
        const messageDate = moment(date);
        const today = moment().startOf("day");
        if (messageDate.isSame(today, "day")) {
            return messageDate.format("HH:mm"); // Displays only the time (ex: 14:30)
        } else {
            return `${messageDate.format("DD/MM/YYYY")} à ${messageDate.format("HH:mm")}`; // Displays the date + time for the other days
        }
    }

    async toggleMessageStar(messageObj) {
        if (messageObj) {
            const data = {
                id: messageObj.id,
                starred: !messageObj.starred,
            };

            const response = await this.$apollo.mutate({
                mutation: STAR_CHAT_MESSAGE,
                variables: data,
            });

            messageObj.starred = response.data.starChatMessage.starred;
        } else {
            debugger;
        }
    }

    getNameColor(messageObj) {
        return this.isNotFromCurrentUser(messageObj) ? "primary" : "secondary";
    }

    getNameTextColor(messageObj) {
        return this.isNotFromCurrentUser(messageObj) ? "text-primary" : "text-secondary";
    }

    getMessageOptionsClass(messageObj) {
        return `message-options ${
            this.isNotFromCurrentUser(messageObj) || this.starredOnly ? "right" : "left"
        }`;
    }

    getFormattedSenderName(messageObj) {
        if (this.isNotFromCurrentUser(messageObj)) {
            return {
                name: `${messageObj.sender.firstname} ${messageObj.sender.lastname}`,
                separator: " - ",
            };
        }
        return { name: "", separator: "" };
    }

    mounted() {
        this.$nextTick(() => {
            this.scrollToLastMessage();
            console.log("Messages container trouvé ?", this.messages);
        });
    }

    // Function that scrolls to the last message
    scrollToLastMessage() {
        const messagesContainer = document.querySelector("#messages-container");
        debugger;
        if (messagesContainer) {
            const messagesContainerHeight = messagesContainer.scrollHeight;
            messagesContainer.scrollTo({
                top: messagesContainerHeight,
            });
        }
    }

    @Watch("filteredMessages", { deep: true })
    onFilteredMessagesChanged() {
        this.$nextTick(() => {
            this.scrollToLastMessage();
        });
    }
}

export default toNative(ChatConversation);
</script>

<style scoped>
.position-relative {
    position: relative;
}

/* .main-content {
    width: 75%;
} */

.message-createdAt {
    display: flex;
    justify-content: flex-end;
}
.message-options {
    display: none;
    position: absolute;
    z-index: 1000;
    top: 0px;
}

.message-options.right {
    right: -36px;
}

.message-options.left {
    left: -36px;
}

.message:hover .message-options {
    display: flex;
}

.triangle {
    width: 0;
    height: 0;
    border-radius: 0px !important;
    position: absolute;
}

.triangle.left {
    border-top: 15px solid whitesmoke;
    border-left: 15px solid transparent;
    left: -14.5px;
    top: 0;
}

.triangle.right {
    border-top: 15px solid rgba(249, 177, 42, 0.2);
    border-right: 15px solid transparent;
    right: -14.5px;
    top: 0;
}

.main-content {
    min-width: 100px;
}

.messages-container {
    overflow: auto;
    margin: 0;
    padding: 0;
    max-height: 300px;
    padding-bottom: 10px;
}
</style>
